import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const DEFAULT_EMAIL = "despachobnt.mty@gmail.com";

let cachedConfig = null;
let cachePromise = null;

/** Carga (y cachea) la config de correo autorizado para el módulo Diseñador. */
export async function fetchDesignerUiAccess() {
  if (cachedConfig) return cachedConfig;
  if (cachePromise) return cachePromise;
  cachePromise = api
    .get("/system/designer-ui-access")
    .then(({ data }) => {
      cachedConfig = {
        authorizedEmail: (data?.authorized_email || DEFAULT_EMAIL).trim(),
        canEdit: Boolean(data?.can_edit),
        seesDesignerModule: Boolean(data?.sees_designer_module),
      };
      return cachedConfig;
    })
    .catch(() => {
      cachedConfig = {
        authorizedEmail: DEFAULT_EMAIL,
        canEdit: false,
        seesDesignerModule: false,
      };
      return cachedConfig;
    })
    .finally(() => {
      cachePromise = null;
    });
  return cachePromise;
}

export function invalidateDesignerUiAccessCache() {
  cachedConfig = null;
}

export function useDesignerUiAccess(user) {
  const [config, setConfig] = useState(cachedConfig);
  const [loading, setLoading] = useState(!cachedConfig);

  const reload = useCallback(() => {
    invalidateDesignerUiAccessCache();
    setLoading(true);
    return fetchDesignerUiAccess().then((c) => {
      setConfig(c);
      setLoading(false);
      return c;
    });
  }, []);

  useEffect(() => {
    if (!user) {
      setConfig(null);
      setLoading(false);
      return;
    }
    reload();
  }, [user, reload]);

  const userEmail = (user?.email || "").trim().toLowerCase();
  const authorized = (config?.authorizedEmail || DEFAULT_EMAIL).trim().toLowerCase();
  const seesDesigner =
    Boolean(config?.seesDesignerModule) ||
    (Boolean(userEmail) && Boolean(authorized) && userEmail === authorized);

  return {
    loading,
    config,
    seesDesignerModule: seesDesigner,
    canEditDesignerAccess: Boolean(config?.canEdit),
    reload,
  };
}

export async function saveDesignerUiAccessEmail(authorizedEmail) {
  const { data } = await api.put("/system/designer-ui-access", {
    authorized_email: authorizedEmail,
  });
  invalidateDesignerUiAccessCache();
  return data;
}

export { DEFAULT_EMAIL as DESIGNER_UI_DEFAULT_EMAIL };

/** True si el correo de sesión coincide con el autorizado para Modo Diseñador. */
export function isDesignerEmail(user, authorizedEmail = DEFAULT_EMAIL) {
  const userEmail = (user?.email || "").trim().toLowerCase();
  const auth = (authorizedEmail || DEFAULT_EMAIL).trim().toLowerCase();
  return Boolean(userEmail && auth && userEmail === auth);
}
